import axios from 'axios';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const RETRYABLE_STATUS_CODES = [502, 503, 504, 408, 429];

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// User-friendly error messages
const ERROR_MESSAGES = {
    NETWORK_ERROR: 'Unable to connect. Please check your internet connection and try again.',
    TIMEOUT: 'The request took too long. Please check your connection and try again.',
    SERVER_ERROR: 'Our servers are experiencing issues. Please try again in a few minutes.',
    FILE_TOO_LARGE: 'The file you uploaded is too large. Please try a smaller file (max 5MB).',
    INVALID_FILE_TYPE: 'This file type is not supported. Please upload a PDF, JPG, or PNG file.',
    UNAUTHORIZED: 'Your session has expired. Please log in again.',
    FORBIDDEN: 'You don\'t have permission to perform this action.',
    NOT_FOUND: 'The requested resource was not found.',
    VALIDATION_ERROR: 'Please check the form for errors and try again.',
    DEFAULT: 'Something went wrong. Please try again.'
};

export const responseSuccessInterceptor = (response) => {
    return response.data;
};

export const responseErrorInterceptor = async (error) => {
    const config = error.config;
    
    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;
    
    // Determine if this is a file upload request
    const isFileUpload = config?.headers?.['Content-Type']?.includes('multipart/form-data') ||
                         config?.data instanceof FormData;
    
    // Check if we should retry
    const shouldRetry = (
        config.__retryCount < MAX_RETRIES &&
        (
            // Retry on network errors (no response)
            !error.response ||
            // Retry on specific status codes (502, 503, 504, etc.)
            RETRYABLE_STATUS_CODES.includes(error.response?.status) ||
            // Retry on CORS errors (often manifests as network error)
            error.message?.includes('Network Error') ||
            error.code === 'ERR_NETWORK'
        )
    );
    
    if (shouldRetry) {
        config.__retryCount += 1;
        
        // Exponential backoff: 1s, 2s, 4s
        const delay = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);
        
        console.log(`Retrying request (${config.__retryCount}/${MAX_RETRIES}) after ${delay}ms...`);
        
        await sleep(delay);
        
        // Create a new axios instance for retry to avoid interceptor loops
        return axios(config);
    }
    
    // Handle 401 unauthorized
    if (error.response?.status === 401) {
        localStorage.removeItem('token');
        // Don't redirect immediately, let the caller handle it
        error.displayMessage = ERROR_MESSAGES.UNAUTHORIZED;
        error.shouldRedirect = true;
        return Promise.reject(error);
    }
    
    // Generate user-friendly error message
    let errorMessage = ERROR_MESSAGES.DEFAULT;
    const status = error.response?.status;
    const errorData = error.response?.data;
    
    // Check for network/connection errors
    if (!error.response || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = isFileUpload 
            ? 'Upload failed due to network issues. Please check your connection and try again.'
            : ERROR_MESSAGES.NETWORK_ERROR;
    }
    // Check for timeout
    else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = isFileUpload
            ? 'File upload timed out. The file might be too large or your connection is slow.'
            : ERROR_MESSAGES.TIMEOUT;
    }
    // HTTP status code specific messages
    else if (status === 413) {
        errorMessage = ERROR_MESSAGES.FILE_TOO_LARGE;
    }
    else if (status === 415) {
        errorMessage = ERROR_MESSAGES.INVALID_FILE_TYPE;
    }
    else if (status === 403) {
        errorMessage = ERROR_MESSAGES.FORBIDDEN;
    }
    else if (status === 404) {
        errorMessage = ERROR_MESSAGES.NOT_FOUND;
    }
    else if (status === 400 || status === 422) {
        // Validation errors - try to extract specific message
        if (errorData?.errors && typeof errorData.errors === 'object') {
            // Handle field-specific errors
            const fieldErrors = Object.entries(errorData.errors);
            if (fieldErrors.length > 0) {
                const [fieldName, messages] = fieldErrors[0];
                const message = Array.isArray(messages) ? messages[0] : messages;
                errorMessage = `${fieldName.replace(/_/g, ' ')}: ${message}`;
            }
        } else if (errorData?.message) {
            errorMessage = errorData.message;
        } else if (errorData?.detail) {
            errorMessage = errorData.detail;
        } else if (errorData?.error) {
            errorMessage = errorData.error;
        } else {
            errorMessage = ERROR_MESSAGES.VALIDATION_ERROR;
        }
    }
    else if (status >= 500) {
        errorMessage = ERROR_MESSAGES.SERVER_ERROR;
    }
    // Try to get message from response
    else if (errorData?.message) {
        errorMessage = errorData.message;
    }
    else if (errorData?.detail) {
        errorMessage = errorData.detail;
    }
    else if (errorData?.error) {
        errorMessage = errorData.error;
    }
    else if (error.message && !error.message.includes('status code')) {
        errorMessage = error.message;
    }
    
    error.displayMessage = errorMessage;
    error.isNetworkError = !error.response || error.code === 'ERR_NETWORK';
    error.isFileUploadError = isFileUpload;
    
    return Promise.reject(error);
};

// Legacy export for backward compatibility
export const responseInterceptor = [responseSuccessInterceptor, responseErrorInterceptor];
